/** @jsx jsx */
import { jsx } from '@emotion/react';

import { Fragment, useState } from 'react';

import { useQuery } from '@apollo/client';
import styled from '@emotion/styled';

import { Checkbox, Dialog, Typography, Typography2 } from '@coursera/cds-core';
import { Box, color } from '@coursera/coursera-ui';

import type { FunctionMapping } from 'bundles/enterprise-admin-skills/components/types';
import AcademySelection from 'bundles/enterprise-skillset-bank/components/SkillSetFinder/AcademySelection';
import BusinessAreaAndRoleSelection from 'bundles/enterprise-skillset-bank/components/SkillSetFinder/BusinessAreaAndRoleSelection';
import GetAllOccupations from 'bundles/enterprise-skillset-bank/queries/GetAllOccupations.graphql';
import type {
  GetAllOccupations_Dedupe1Query as GetAllOccupationsQueryData,
  GetAllOccupations_Dedupe1QueryVariables,
} from 'bundles/enterprise-skillset-bank/queries/__generated__/GetAllOccupations';
import { fallbackFunctionId } from 'bundles/enterprise-skillset-bank/utils/academies';
import { Button } from 'bundles/enterprise-ui/components/Button';
import { Spacer } from 'bundles/enterprise-ui/components/Spacer';
import { unslugify } from 'bundles/enterprise-ui/utils';

import _t from 'i18n!nls/enterprise-admin-skills';

const Panel = styled.div({
  background: color.bgGray,
  border: `1px solid ${color.divider}`,
  padding: '12px 18px',
});

const BusinessAreaAndRoleSelectionHeader: React.FC<{
  areAllDepartmentsSelected: boolean;
  toggleAllDepartments: () => void;
}> = ({ areAllDepartmentsSelected, toggleAllDepartments }) => (
  <div css={{ padding: '0 18px' }}>
    <div css={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold">{_t('Select departments and roles')}</Typography>
      <Checkbox
        checked={areAllDepartmentsSelected}
        onChange={toggleAllDepartments}
        label={areAllDepartmentsSelected ? _t('Deselect all departments') : _t('Select all departments')}
      />
    </div>
    <Typography2 component="p">
      {_t('Select the roles and business areas  you’d like to see SkillSets for')}
    </Typography2>
  </div>
);

const LaunchSkillSetsFinder: React.FC<{ onLaunch: () => void }> = ({ onLaunch }) => (
  <Panel>
    <Box justifyContent="between" alignItems="end">
      <section>
        {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
        <Typography variant="h2">{_t('SkillSet Finder')}</Typography>
        <Typography2 component="p">
          {_t(
            'Filter by business areas and roles to find the SkillSets to meet your learning needs, with our SkillSet Finder'
          )}
        </Typography2>
      </section>
      <Spacer size={24} />
      <Button variant="secondary" size="small" onClick={onLaunch} data-e2e="launch-skillset-finder">
        {_t('Launch SkillSet Finder')}
      </Button>
    </Box>
  </Panel>
);

const Filter: React.FC<{
  name: string;
  values?: string[];
}> = ({ name, values }) => (
  <div>
    <Typography2 component="strong">{name}</Typography2>
    <Spacer size={12} />
    <Typography2 component="p">
      {values
        ?.slice(0, 2)
        .map(unslugify)
        .concat(values?.length > 2 ? [_t('+ #{number} more', { number: values?.length - 2 })] : [])
        .join(', ') || '–'}
    </Typography2>
  </div>
);

const ExtraFiltersSection = styled.div({
  border: `1px solid ${color.divider}`,
  padding: '18px',
});

const ExtraFilters: React.FC<{
  filters: {
    academyIds?: string[];
    functionIds?: string[];
    occupationIds?: string[];
  };
  onChangeFilters: () => void;
  onClearAllFilters: () => void;
}> = ({ filters: { academyIds, functionIds, occupationIds }, onChangeFilters, onClearAllFilters }) => {
  return (
    <ExtraFiltersSection>
      {/* eslint-disable-next-line @coursera-web/custom-rules/prefer-cds-typography2 -- FIXME: legacy; migrate to Typography2 */}
      <Typography variant="h2semibold">{_t('SkillSet Finder filters')}</Typography>
      <Spacer size={12} />
      <Box justifyContent="between" alignItems="end">
        <Filter name={_t('Skills academies')} values={academyIds} />
        <Filter name={_t('Business areas')} values={functionIds} />
        <Filter name={_t('Roles')} values={occupationIds} />
        <Box flexDirection="column" alignItems="center">
          <Button variant="secondary" onClick={onChangeFilters}>
            {_t('Change filters')}
          </Button>
          <Spacer size={12} />
          <Button variant="ghost" onClick={onClearAllFilters}>
            {_t('Clear all filters')}
          </Button>
        </Box>
      </Box>
    </ExtraFiltersSection>
  );
};

type Filters = {
  academyIds?: string[];
  functionIds?: string[];
  occupationIds?: string[];
};

type PropsFromCaller = {
  allSkillSets?: {
    academyIds?: string[];
    functionId: string;
    occupationId: string;
  }[];
  filters: Filters;
  onChange: (filters: Filters) => void;
  isFinderOpenInitially?: boolean;
  functionMappings?: FunctionMapping[];
};

type Props = PropsFromCaller;

const SkillSetFinder = ({ allSkillSets, filters, onChange, isFinderOpenInitially, functionMappings }: Props) => {
  const [isFinderOpen, setIsFinderOpen] = useState<boolean>(isFinderOpenInitially || false);
  const [isSelectingAcademy, setIsSelectingAcademy] = useState<boolean>(true);
  const [selectedAcademyIds, setSelectedAcademyIds] = useState<string[]>([]);
  const [selectedDepartmentIds, setSelectedDepartmentIds] = useState<string[]>([]);
  const [selectedOccupationIds, setSelectedOccupationIds] = useState<string[]>([]);
  const [currentFilters, setCurrentFilters] = useState<Filters>({});

  const { data } = useQuery<GetAllOccupationsQueryData, GetAllOccupations_Dedupe1QueryVariables>(GetAllOccupations, {
    context: { clientName: 'gatewayGql' },
  });

  const occupationMappings = data?.Occupations?.getAllOccupations?.elements ?? [];

  const toggleFinder = () => {
    setIsFinderOpen((prevState) => !prevState);
    setIsSelectingAcademy(selectedAcademyIds.length === 0);
    setSelectedAcademyIds(filters.academyIds ?? []);
    setSelectedDepartmentIds(filters.functionIds ?? []);
    setSelectedOccupationIds(filters.occupationIds ?? []);
  };

  const handleClearAllFilters = () => {
    const appliedFilters: Filters = {};
    setCurrentFilters(appliedFilters);
    onChange(appliedFilters);
  };

  const handleFindSkillSets = () => {
    const appliedFilters: Filters = {};
    if (selectedAcademyIds.length) {
      appliedFilters.academyIds = selectedAcademyIds;
    }
    if (selectedDepartmentIds.length) {
      appliedFilters.functionIds = selectedDepartmentIds;
    }
    if (selectedOccupationIds.length) {
      appliedFilters.occupationIds = selectedOccupationIds;
    }

    setCurrentFilters(appliedFilters);
    onChange(appliedFilters);
    toggleFinder();
  };

  const getSkillFinderData = () => {
    return (
      allSkillSets?.reduce(
        (byDepartment, skillset) => {
          if (!skillset.occupationId) {
            return byDepartment;
          }
          const department = byDepartment.find((_) => _.department === (skillset.functionId ?? fallbackFunctionId));
          const academyIds = skillset.academyIds ?? [];
          if (department) {
            academyIds.forEach((academyId) => {
              const academy = department.academies.find((_) => _.academy === academyId);
              if (academy) {
                if (!academy.occupations.includes(skillset.occupationId)) {
                  academy.occupations.push(skillset.occupationId);
                }
              } else {
                department.academies.push({
                  academy: academyId,
                  occupations: [skillset.occupationId],
                });
              }
            });
          } else {
            byDepartment.push({
              department: skillset.functionId ?? fallbackFunctionId,
              academies: academyIds.map((academyId) => ({
                academy: academyId,
                occupations: [skillset.occupationId],
              })),
            });
          }
          return byDepartment;
        },
        [] as {
          department: string;
          academies: { academy: string; occupations: string[] }[];
        }[]
      ) ?? []
    );
  };

  const areExtraFiltersApplied = Boolean(currentFilters?.academyIds?.length);
  const skillFinderData = getSkillFinderData();
  const allDepartmentIds = skillFinderData.reduce((ids, _) => {
    if (_.department && _.academies.some(({ academy }) => selectedAcademyIds.includes(academy))) {
      ids.push(_.department);
    }
    return ids;
  }, [] as string[]);
  const areAllDepartmentsSelected = allDepartmentIds.every((id) => selectedDepartmentIds.includes(id));
  const onContinueWithAcademy =
    selectedAcademyIds.length > 0
      ? () => {
          setIsSelectingAcademy(false);
          window.setTimeout(() => {
            document.querySelector('#business-area-and-role-selection')?.scrollIntoView?.({
              block: 'start',
              behavior: 'smooth',
            });
          }, 0);
        }
      : undefined;

  return (
    <div data-e2e="skillset-finder">
      {!areExtraFiltersApplied && (
        <div>
          <LaunchSkillSetsFinder onLaunch={toggleFinder} />
          <Spacer size={24} />
        </div>
      )}
      {areExtraFiltersApplied && (
        <div>
          <ExtraFilters
            filters={currentFilters}
            onChangeFilters={toggleFinder}
            onClearAllFilters={handleClearAllFilters}
          />
          <Spacer size={24} />
        </div>
      )}
      <Dialog width="large" open={isFinderOpen} onClose={toggleFinder} sticky="actions">
        <Dialog.HeadingGroup supportText={_t('Choose a skill area in which you’d like to develop and track skills')}>
          {_t('SkillSet Finder')}
        </Dialog.HeadingGroup>
        <Dialog.Content>
          <Fragment>
            <AcademySelection
              readOnly={!isSelectingAcademy}
              selectedAcademyIds={selectedAcademyIds}
              onChange={(ids) => {
                setSelectedAcademyIds(ids);
              }}
              onChangeSelections={() => {
                setIsSelectingAcademy(true);
                setSelectedDepartmentIds([]);
                setSelectedOccupationIds([]);
              }}
            />
            {!isSelectingAcademy && (
              <div id="business-area-and-role-selection">
                <Spacer size={32} />
                <BusinessAreaAndRoleSelectionHeader
                  areAllDepartmentsSelected={areAllDepartmentsSelected}
                  toggleAllDepartments={() => {
                    setSelectedDepartmentIds(areAllDepartmentsSelected ? [] : allDepartmentIds);
                    setSelectedOccupationIds([]);
                  }}
                />
                <Spacer size={24} />
                <BusinessAreaAndRoleSelection
                  skillFinderData={skillFinderData}
                  selectedAcademyIds={selectedAcademyIds}
                  selectedDepartmentIds={selectedDepartmentIds}
                  selectedOccupationIds={selectedOccupationIds}
                  onOccupationIdsChange={(ids: string[]) => {
                    setSelectedOccupationIds(ids);
                  }}
                  onDepartmentIdsChange={(ids: string[]) => {
                    setSelectedDepartmentIds(ids);
                  }}
                  occupationMappings={occupationMappings}
                  functionMappings={functionMappings}
                />
              </div>
            )}
          </Fragment>
        </Dialog.Content>
        <Dialog.Actions css={{ display: 'flex', gap: 24 }}>
          <Button variant="secondary" onClick={toggleFinder}>
            {_t('Cancel')}
          </Button>
          {isSelectingAcademy ? (
            <Button
              variant="primary"
              onClick={onContinueWithAcademy}
              data-e2e="continue-with-selected-academies"
              disabled={!onContinueWithAcademy}
            >
              {_t('Continue')}
            </Button>
          ) : (
            <Button variant="primary" onClick={handleFindSkillSets} data-e2e="find-skillsets">
              {_t('Find SkillSets')}
            </Button>
          )}
        </Dialog.Actions>
      </Dialog>
    </div>
  );
};

export default SkillSetFinder;
